"use client";

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  motion,
  useInView,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import Image from "next/image";
import { cn } from "@marketing-sites/utils/cn";

import { DemoInViewContext } from "./hooks";
import { InboxConversation, InboxFrame, InboxList } from "./inbox";
import { Messenger } from "./messenger";
import { MobileHeroTabs } from "./mobile-tabs";
import { Kicker, PRODUCT_FONT } from "./primitives";
import { TICKETS, type DemoPlacement, type Industry } from "./tickets";

// The blue swirl behind the /helpdesk hero.
const SWIRL_SRC =
  "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/5fb37c5042220b46fd34e50b2c70ac1ff3071481-4520x2522.jpg";

// Keyed by industry so each page that adopts the demo names its own events.
const SECTION_IDS: Record<Industry, string> = {
  ecommerce: "sec_solutions_ecommerce_product_demo",
  technology: "sec_solutions_technology_product_demo",
  gaming: "sec_solutions_gaming_product_demo",
  fintech: "sec_solutions_fintech_product_demo",
};
const HOME_SECTION_IDS: Record<Industry, string> = {
  ecommerce: "sec_home_ecommerce_product_demo",
  technology: "sec_home_technology_product_demo",
  gaming: "sec_home_gaming_product_demo",
  fintech: "sec_home_fintech_product_demo",
};

const PAINT_ABOVE_FOLD = 480;

// On desktop the paint starts a fixed distance above the fold, so tall
// screens see a band of it rather than a wall.
function usePaintTop(stageRef: React.RefObject<HTMLDivElement | null>) {
  const [top, setTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const update = () => {
      const stage = stageRef.current;
      if (!stage || !desktop.matches) return setTop(null);
      const stageTop = stage.getBoundingClientRect().top + window.scrollY;
      const offset = window.innerHeight - PAINT_ABOVE_FOLD - stageTop;
      setTop(Math.min(Math.max(offset, -160), stage.offsetHeight * 0.55));
    };
    update();
    window.addEventListener("resize", update);
    desktop.addEventListener("change", update);
    return () => {
      window.removeEventListener("resize", update);
      desktop.removeEventListener("change", update);
    };
  }, [stageRef]);
  return top;
}

/** The live helpdesk inbox and Fin Messenger, playing one industry's tickets. */
export function ProductDemo({
  industry,
  placement = "solutions",
  heading,
  className,
}: {
  industry: Industry;
  placement?: DemoPlacement;
  heading?: ReactNode;
  className?: string;
}) {
  const tickets = TICKETS[industry];
  const [activeIndex, setActiveIndex] = useState(0);
  const demoRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const { scrollY } = useScroll();
  // The paint sinks behind the product as the page scrolls, so it frames the
  // demo above the fold without competing with it further down.
  const paintY = useTransform(scrollY, [0, 900], [0, reduceMotion ? 0 : 320]);
  const paintTop = usePaintTop(stageRef);
  const inView = useInView(demoRef, { once: true, amount: 0.35 });

  const select = (index: number) => setActiveIndex(index);
  const ticket = tickets[activeIndex] ?? tickets[0];

  return (
    <>
      {heading && (
        // Matches the demo's own width below, so the heading lines up with its left edge.
        <div className="col-span-full mx-auto mb-10 w-full max-w-[720px] [--demo-zoom:1] lg:max-w-[calc(1140px*var(--demo-zoom)+1.5rem)] lg:max-[1439px]:[--demo-zoom:0.92]">
          {heading}
        </div>
      )}
      <div
        ref={stageRef}
        data-analytics-region="Product demo"
        data-analytics-id={
          placement === "home"
            ? HOME_SECTION_IDS[industry]
            : SECTION_IDS[industry]
        }
        className={cn(
          "relative col-span-full mt-12 md:mt-16 lg:mt-20",
          className,
        )}
      >
        {placement === "solutions" && (
          <div
            aria-hidden
            style={paintTop === null ? undefined : { top: paintTop }}
            className="lg:-bottom-25 pointer-events-none absolute -bottom-14 left-1/2 top-8 -z-10 w-screen -translate-x-1/2 overflow-hidden mix-blend-multiply md:-bottom-32 md:top-1/2 lg:-top-40"
          >
            <motion.div
              style={{ y: paintY }}
              className="absolute left-1/2 top-0 aspect-[2260/1261] w-[840px] -translate-x-1/2 md:w-[max(110vw,2000px)]"
            >
              <Image
                src={SWIRL_SRC}
                alt=""
                fill
                priority
                sizes="(min-width: 1818px) 110vw, (min-width: 768px) 2000px, 840px"
                className="object-cover"
              />
            </motion.div>
          </div>
        )}
        <div className="lg:hidden">
          <MobileHeroTabs industry={industry} placement={placement} />
        </div>
        <DemoInViewContext value={inView}>
          <div
            ref={demoRef}
            className="hidden gap-8 [--demo-zoom:1] lg:grid lg:grid-cols-[minmax(0,calc(780px*var(--demo-zoom)))_calc(360px*var(--demo-zoom))] lg:justify-center lg:gap-6 lg:max-[1439px]:[--demo-zoom:0.92]"
          >
            <div className="flex min-w-0 flex-col">
              <Kicker product="helpdesk" />
              <InboxFrame
                className={cn(
                  "md:h-[700px] lg:[zoom:var(--demo-zoom)]",
                  PRODUCT_FONT,
                )}
              >
                <InboxList
                  tickets={tickets}
                  activeIndex={activeIndex}
                  onSelect={select}
                  placement={placement}
                />
                <InboxConversation
                  ticket={ticket}
                  industry={industry}
                  placement={placement}
                />
              </InboxFrame>
            </div>
            <div className="flex w-full min-w-0 flex-col md:mx-auto md:max-w-[420px] lg:max-w-none">
              <Kicker product="fin" className="lg:self-end" />
              <div
                className={cn(
                  "h-[520px] lg:h-[700px] lg:[zoom:var(--demo-zoom)]",
                  PRODUCT_FONT,
                )}
              >
                <Messenger
                  ticket={ticket}
                  industry={industry}
                  placement={placement}
                  showLiveDemo
                />
              </div>
            </div>
          </div>
        </DemoInViewContext>
      </div>
    </>
  );
}
